import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, UserX } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import styles from './StaffPage.module.css';

const getStaff = () => api.get('/admin/staff').then(r => r.data.data);
const inviteStaff = (data: { email: string; name: string; phone?: string }) =>
  api.post('/admin/staff', data).then(r => r.data.data);
const disableStaff = (username: string) =>
  api.delete(`/admin/staff/${encodeURIComponent(username)}`).then(r => r.data.data);

export default function StaffPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', phone: '' });

  const { data, isLoading } = useQuery({ queryKey: ['admin-staff'], queryFn: getStaff });

  const inviteMutation = useMutation({
    mutationFn: inviteStaff,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-staff'] });
      setShowForm(false);
      setForm({ email: '', name: '', phone: '' });
      toast.success('Staff member invited! They will receive a temporary password via email.');
    },
    onError: () => toast.error('Failed to invite staff member'),
  });

  const disableMutation = useMutation({
    mutationFn: disableStaff,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-staff'] }); toast.success('Staff account disabled'); },
    onError: () => toast.error('Failed to disable account'),
  });

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>{t('admin.staff')}</h1>
        <button className={styles.inviteBtn} onClick={() => setShowForm(v => !v)}>
          <UserPlus size={16} /> {t('admin.invite_staff')}
        </button>
      </div>

      {showForm && (
        <div className={styles.formCard}>
          <h2 className={styles.formTitle}>Invite Staff Member</h2>
          <form className={styles.form} onSubmit={e => { e.preventDefault(); inviteMutation.mutate(form); }}>
            <div className={styles.field}><label>Full Name *</label><input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required /></div>
            <div className={styles.field}><label>Email *</label><input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} required /></div>
            <div className={styles.field}><label>Phone (optional)</label><input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} /></div>
            <div className={styles.formActions}>
              <button type="button" onClick={() => setShowForm(false)} className={styles.cancelBtn}>Cancel</button>
              <button type="submit" className={styles.saveBtn} disabled={inviteMutation.isPending}>
                {inviteMutation.isPending ? 'Inviting...' : 'Send Invite'}
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <p className={styles.loading}>Loading staff...</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {(data?.staff || []).map((s: Record<string, unknown>) => (
                <tr key={s.username as string}>
                  <td className={styles.name}>{s.name as string || '—'}</td>
                  <td>{s.email as string}</td>
                  <td>{s.phone as string || '—'}</td>
                  <td>
                    <span className={s.enabled ? styles.activeBadge : styles.disabledBadge}>
                      {s.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    {s.enabled ? (
                      <button
                        className={styles.disableBtn}
                        onClick={() => { if (confirm(`Disable ${s.email}?`)) disableMutation.mutate(s.username as string); }}
                      >
                        <UserX size={15} /> Disable
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
